import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useTranslation } from 'react-i18next';
import {
  DirtyFormAlert,
  InputErrorMessage,
  Label,
  Modal,
  MultiEmailInput,
  Notification,
} from 'shared/components';
import {
  getApiErrorMessage,
  getUnsubmittedEmailError,
  normalizeEmail,
  toChunks,
  uniqueEmails,
} from 'shared/helpers';
import { updateOrderManagers } from 'orders/OrderContainer/actions';
import ManagerPermissionsPicker from 'orders/OrderContainer/components/OrderManagers/components/ManagerPermissionsPicker';
import './styles.scss';

// Up to 20 emails can be entered in each list. update_managers accepts at most
// 10 orders and 10 emails in each list per request, so both are sent in groups
const MAX_EMAILS = 20;
const ORDERS_PER_REQUEST = 10;
const EMAILS_PER_REQUEST = 10;
const PREVIEWED_ORDERS = 5;

// The response is per order, the result view is per email so a replaced
// manager can be checked at a glance
const getEmailOrders = (results, outcome, email, orderLabels) =>
  results
    .filter((orderResult) =>
      get(orderResult, outcome, []).some(
        (e) => normalizeEmail(e) === normalizeEmail(email),
      ),
    )
    .map(
      (orderResult) =>
        orderLabels[orderResult.order_id] || orderResult.order_id,
    );

const UpdateOrderManagersForm = ({ orders, closeCb, confirmCb }) => {
  const { t } = useTranslation();
  const [dirty, setDirty] = useState(false);
  const [isDirtyFormAlertDisplayed, setDirtyFormAlertDisplay] = useState(false);
  const [loading, setLoading] = useState(false);
  const [removeEmails, setRemoveEmails] = useState({});
  const [addEmails, setAddEmails] = useState({});
  const [permissions, setPermissions] = useState(null);
  const [permissionsError, setPermissionsError] = useState('');
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [results, setResults] = useState(null);
  const [submitted, setSubmitted] = useState({ remove: [], add: [] });
  const [progress, setProgress] = useState(null);
  const [partialError, setPartialError] = useState(null);
  // The list clears its selection after saving, the result view still needs the orders
  const [selectedOrders] = useState(orders);

  const orderLabels = selectedOrders.reduce(
    (labels, order) => ({ ...labels, [order.id]: order.store_id || order.id }),
    {},
  );
  const orderPreview = selectedOrders
    .slice(0, PREVIEWED_ORDERS)
    .map((order) => orderLabels[order.id])
    .join(', ');
  const hiddenOrdersCount = selectedOrders.length - PREVIEWED_ORDERS;
  const hasAddEmails = get(addEmails, 'emails.length', 0) > 0;

  const validate = (toRemove, toAdd) => {
    const removeError = getUnsubmittedEmailError(removeEmails.value);
    const addError = getUnsubmittedEmailError(addEmails.value);
    setRemoveEmails({ ...removeEmails, validationError: removeError });
    setAddEmails({ ...addEmails, validationError: addError });
    if (removeError || addError) {
      return false;
    }
    if (!toRemove.length && !toAdd.length) {
      setFormError(t('Enter at least one email to remove or add'));
      return false;
    }
    if (toRemove.length > MAX_EMAILS || toAdd.length > MAX_EMAILS) {
      setFormError(t('Enter at most 20 emails in each list'));
      return false;
    }
    const inBoth = toRemove.filter((email) =>
      toAdd.some((e) => normalizeEmail(e) === normalizeEmail(email)),
    );
    if (inBoth.length) {
      setFormError(
        `${t('The same email can not be removed and added')}: ${inBoth.join(', ')}`,
      );
      return false;
    }
    if (toAdd.length && Array.isArray(permissions) && !permissions.length) {
      setPermissionsError(t('Select at least one permission'));
      return false;
    }
    setFormError('');
    return true;
  };

  const getError = (err, isFirstRequest) => {
    if (get(err, 'response.status') === 404) {
      return isFirstRequest
        ? t(
            'You do not manage one or more of the selected orders, so nothing was changed',
          )
        : t('One or more of the selected orders can not be used.');
    }
    return (
      getApiErrorMessage(err) ||
      t('There was an error while saving your changes')
    );
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    if (results) {
      return closeCb();
    }

    const toRemove = uniqueEmails(get(removeEmails, 'emails', []));
    const toAdd = uniqueEmails(get(addEmails, 'emails', []));
    if (!validate(toRemove, toAdd)) {
      return false;
    }

    setLoading(true);
    setSubmitted({ remove: toRemove, add: toAdd });
    // The n-th batch of removals goes with the n-th batch of additions. Each
    // email is in one batch only, so the per-order results need no merging
    const removeBatches = toChunks(toRemove, EMAILS_PER_REQUEST);
    const addBatches = toChunks(
      toAdd.map((email) => (permissions ? { email, permissions } : { email })),
      EMAILS_PER_REQUEST,
    );
    const changeBatches = Array.from(
      { length: Math.max(removeBatches.length, addBatches.length) },
      (_, i) => ({ remove: removeBatches[i] || [], add: addBatches[i] || [] }),
    );
    const orderGroups = toChunks(selectedOrders, ORDERS_PER_REQUEST);
    const total = orderGroups.length * changeBatches.length;
    const data = [];
    let done = 0;
    for (const [groupIndex, orderGroup] of orderGroups.entries()) {
      for (const changes of changeBatches) {
        setProgress({ done, total });
        try {
          const res = await updateOrderManagers({
            ...changes,
            order_ids: orderGroup.map((order) => order.id),
          });
          data.push(...get(res, 'data', []));
        } catch (err) {
          setProgress(null);
          setLoading(false);
          if (done === 0) {
            setFormError(getError(err, true));
            return false;
          }
          // Requests before this one are saved, so their results are still shown.
          // Orders that were not fully processed stay selected, so they can be retried
          const notProcessedOrders = orderGroups.slice(groupIndex).flat();
          confirmCb(notProcessedOrders);
          setPartialError({
            message: getError(err, false),
            orders: notProcessedOrders.map((order) => orderLabels[order.id]),
          });
          setResults(data);
          return true;
        }
        done += 1;
      }
    }
    setProgress(null);
    setLoading(false);
    confirmCb([]);

    const hasSkipped = data.some(
      (order) =>
        get(order, 'skipped_not_manager.length') ||
        get(order, 'skipped_already_manager.length'),
    );
    if (!hasSkipped) {
      Notification(
        'success',
        t('Changes saved successfully'),
        t('License managers have been updated on the selected orders'),
      );
      return closeCb();
    }
    setResults(data);
    return true;
  };

  const handleClose = () => {
    if (loading) {
      return false;
    }
    if (!dirty || results) {
      return closeCb();
    }
    return setDirtyFormAlertDisplay(true);
  };

  const renderOutcome = (email, outcome, label, className) => {
    const emailOrders = getEmailOrders(results, outcome, email, orderLabels);
    if (!emailOrders.length) {
      return null;
    }
    return (
      <tr key={outcome} className={className}>
        <td className="outcome-label">{label}</td>
        <td className="outcome-count">{emailOrders.length}</td>
        <td>
          <details>
            <summary>{t('Show orders')}</summary>
            <div className="outcome-orders">{emailOrders.join(', ')}</div>
          </details>
        </td>
      </tr>
    );
  };

  const renderEmailResult = (email, rows) => (
    <div key={email} className="UpdateOrderManagersForm-result">
      <div className="result-email">{email}</div>
      <table>
        <thead>
          <tr>
            <th>{t('Result')}</th>
            <th>{t('Orders')}</th>
            <th />
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );

  return (
    <Modal
      title={t('Update managers')}
      closeCb={handleClose}
      confirmCb={handleSubmit}
      confirmText={results ? t('Close') : t('Update managers')}
      disabled={loading || permissionsLoading}
      size="lg"
    >
      <div className="UpdateOrderManagersForm">
        <div className="UpdateOrderManagersForm-orders">
          <strong>{`${t('Selected orders')}: ${selectedOrders.length}`}</strong>
          <span>
            {orderPreview}
            {hiddenOrdersCount > 0 && ` +${hiddenOrdersCount}`}
          </span>
        </div>
        {results ? (
          <div className="UpdateOrderManagersForm-results">
            {partialError && (
              <div className="UpdateOrderManagersForm-warning">
                <p>{partialError.message}</p>
                <p>
                  {`${t('These orders were not fully processed')}: ${partialError.orders.slice(0, PREVIEWED_ORDERS).join(', ')}`}
                  {partialError.orders.length > PREVIEWED_ORDERS &&
                    ` +${partialError.orders.length - PREVIEWED_ORDERS}`}
                </p>
                <p>
                  {t(
                    'They are still selected in the list, so you can try again.',
                  )}
                </p>
              </div>
            )}
            <p className="UpdateOrderManagersForm-help">
              {t(
                'Changes are saved. Orders where a manager was already in the requested state were left as they were.',
              )}
            </p>
            {submitted.remove.map((email) =>
              renderEmailResult(email, [
                renderOutcome(email, 'removed', t('Removed'), 'is-done'),
                renderOutcome(
                  email,
                  'skipped_not_manager',
                  t('Was not a manager, nothing to remove'),
                  'is-skipped',
                ),
              ]),
            )}
            {submitted.add.map((email) =>
              renderEmailResult(email, [
                renderOutcome(email, 'added', t('Added'), 'is-done'),
                renderOutcome(
                  email,
                  'skipped_already_manager',
                  t('Was already a manager, nothing to add'),
                  'is-skipped',
                ),
              ]),
            )}
          </div>
        ) : (
          <div>
            <div className="UpdateOrderManagersForm-section">
              <Label text={t('Remove managers')} inputId="remove-emails" />
              <p className="UpdateOrderManagersForm-help">
                {t(
                  'These people stop being managers of the selected orders. Managers who manage an order only through its customer account are not changed.',
                )}
              </p>
              <MultiEmailInput
                emails={get(removeEmails, 'emails')}
                onEmailSubmit={(val) => {
                  setDirty(true);
                  setFormError('');
                  setRemoveEmails(val);
                }}
                isDisabled={loading}
              />
              <InputErrorMessage
                text={get(removeEmails, 'validationError', '')}
              />
            </div>
            <div className="UpdateOrderManagersForm-section">
              <Label text={t('Add managers')} inputId="add-emails" />
              <p className="UpdateOrderManagersForm-help">
                {t('These people become managers of the selected orders.')}
              </p>
              <MultiEmailInput
                emails={get(addEmails, 'emails')}
                onEmailSubmit={(val) => {
                  setDirty(true);
                  setFormError('');
                  setAddEmails(val);
                }}
                isDisabled={loading}
              />
              <InputErrorMessage text={get(addEmails, 'validationError', '')} />
              {hasAddEmails && (
                <ManagerPermissionsPicker
                  permissions={permissions}
                  onChange={(val) => {
                    setDirty(true);
                    setPermissions(val);
                    setPermissionsError('');
                  }}
                  onLoadingChange={setPermissionsLoading}
                  error={permissionsError}
                  disabled={loading}
                />
              )}
            </div>
            {selectedOrders.length > ORDERS_PER_REQUEST && (
              <p className="UpdateOrderManagersForm-help">
                {t('The selected orders are sent in groups of 10.')}
              </p>
            )}
            {progress && progress.total > 1 && (
              <div className="UpdateOrderManagersForm-progress">
                {`${t('Processing')} ${progress.done + 1} / ${progress.total}`}
              </div>
            )}
            <InputErrorMessage text={formError} />
          </div>
        )}
      </div>
      {isDirtyFormAlertDisplayed && (
        <DirtyFormAlert
          dirty={dirty}
          closeAlert={() => setDirtyFormAlertDisplay(false)}
          closeCb={closeCb}
        />
      )}
    </Modal>
  );
};

UpdateOrderManagersForm.propTypes = {
  orders: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      store_id: PropTypes.string,
    }),
  ).isRequired,
  closeCb: PropTypes.func.isRequired,
  confirmCb: PropTypes.func.isRequired,
};

export default UpdateOrderManagersForm;
